import { useState, useCallback } from 'react';

export type SortDir = 'asc' | 'desc';

export interface SortState<K extends string> {
  key: K;
  dir: SortDir;
}

export function useTableSort<K extends string>(defaultKey: K, defaultDir: SortDir = 'asc') {
  const [sort, setSort] = useState<SortState<K>>({ key: defaultKey, dir: defaultDir });

  const toggle = useCallback((key: K) => {
    setSort(prev =>
      prev.key === key
        ? { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' }
        : { key, dir: 'asc' },
    );
  }, []);

  return { sort, toggle } as const;
}

const heCollator = new Intl.Collator('he', { sensitivity: 'base' });

export function sortRows<T>(rows: T[], key: string, dir: SortDir): T[] {
  const sorted = [...rows].sort((a, b) => {
    const av = (a as Record<string, unknown>)[key];
    const bv = (b as Record<string, unknown>)[key];

    if (av == null && bv == null) return 0;
    if (av == null) return 1;
    if (bv == null) return -1;

    if (typeof av === 'number' && typeof bv === 'number') return av - bv;
    return heCollator.compare(String(av), String(bv));
  });
  return dir === 'desc' ? sorted.reverse() : sorted;
}
